import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'apollo-admin-auth',
    flowType: 'pkce',
    debug: false,
    // Refresh tokens when they have less than 30 minutes remaining
    // This prevents premature refreshes and supports 6-hour sessions
  },
  global: {
    headers: {
      'x-my-custom-header': 'apollo-satellite-admin',
      'x-client-info': 'apollo-satellite@1.0.0',
      'Connection': 'keep-alive'
    },
    fetch: async (url, options = {}) => {
      const maxRetries = 3;
      let lastError: Error | null = null;

      // Check if this is an auth-related request
      const isAuthRequest = url.includes('/auth/');

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const controller = new AbortController();
          // Use longer timeout for auth requests during visibility changes
          const baseTimeout = isAuthRequest ? 90000 : 75000;
          const timeoutWithBackoff = baseTimeout * (1 + attempt * 0.5);

          const timeoutId = setTimeout(() => {
            controller.abort();
          }, timeoutWithBackoff);

          const response = await fetch(url, {
            ...options,
            signal: controller.signal,
            keepalive: true,
            // Add credentials to ensure cookies are sent
            credentials: 'same-origin',
          });

          clearTimeout(timeoutId);
          return response;

        } catch (error: any) {
          lastError = error;

          if (error.name === 'AbortError' && attempt < maxRetries - 1) {
            const backoffDelay = Math.min(1000 * Math.pow(2, attempt), 5000);
            if (!document.hidden) {
              console.warn(`Supabase request timeout (attempt ${attempt + 1}/${maxRetries}), retrying in ${backoffDelay}ms...`);
            }
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            continue;
          }

          if (error.name === 'AbortError') {
            throw new Error('Connection timeout - server may be slow or unreachable. Please try again.');
          }

          if (attempt < maxRetries - 1) {
            const backoffDelay = Math.min(1000 * Math.pow(2, attempt), 5000);
            if (!document.hidden) {
              console.warn(`Supabase request error (attempt ${attempt + 1}/${maxRetries}): ${error.message}, retrying in ${backoffDelay}ms...`);
            }
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            continue;
          }

          throw error;
        }
      }

      throw lastError || new Error('Request failed after multiple attempts');
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Database types (will be auto-generated from schema)
export interface Database {
  public: {
    Tables: {
      orders: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          order_number: string;
          order_status: 'pending' | 'processing' | 'shipped' | 'completed' | 'cancelled' | 'refunded';
          customer_email: string;
          customer_phone: string | null;
          customer_name: string | null;
          total_amount: number;
          currency: string;
          shipping_cost: number;
          order_type: 'purchase' | 'reload' | 'mixed';
          has_reloads: boolean;
          has_physical_items: boolean;
          items: any; // JSONB
          shipping_address: any | null; // JSONB
          billing_address: any | null; // JSONB
          stripe_session_id: string | null;
          stripe_payment_intent_id: string | null;
          stripe_metadata: any | null; // JSONB
          fulfillment_notes: string | null;
          tracking_number: string | null;
          shipped_at: string | null;
          completed_at: string | null;
          refund_id: string | null;
          refund_amount: number;
          refund_status: 'none' | 'partial' | 'full' | 'pending' | 'failed';
          refunded_at: string | null;
          refund_reason: string | null;
          refund_metadata: any; // JSONB
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          order_number: string;
          order_status?: 'pending' | 'processing' | 'shipped' | 'completed' | 'cancelled' | 'refunded';
          customer_email: string;
          customer_phone?: string | null;
          customer_name?: string | null;
          total_amount: number;
          currency?: string;
          shipping_cost?: number;
          order_type: 'purchase' | 'reload' | 'mixed';
          has_reloads?: boolean;
          has_physical_items?: boolean;
          items: any; // JSONB
          shipping_address?: any | null; // JSONB
          billing_address?: any | null; // JSONB
          stripe_session_id?: string | null;
          stripe_payment_intent_id?: string | null;
          stripe_metadata?: any | null; // JSONB
          fulfillment_notes?: string | null;
          tracking_number?: string | null;
          shipped_at?: string | null;
          completed_at?: string | null;
          refund_id?: string | null;
          refund_amount?: number;
          refund_status?: 'none' | 'partial' | 'full' | 'pending' | 'failed';
          refunded_at?: string | null;
          refund_reason?: string | null;
          refund_metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          order_number?: string;
          order_status?: 'pending' | 'processing' | 'shipped' | 'completed' | 'cancelled' | 'refunded';
          customer_email?: string;
          customer_phone?: string | null;
          customer_name?: string | null;
          total_amount?: number;
          currency?: string;
          shipping_cost?: number;
          order_type?: 'purchase' | 'reload' | 'mixed';
          has_reloads?: boolean;
          has_physical_items?: boolean;
          items?: any; // JSONB
          shipping_address?: any | null; // JSONB
          billing_address?: any | null; // JSONB
          stripe_session_id?: string | null;
          stripe_payment_intent_id?: string | null;
          stripe_metadata?: any | null; // JSONB
          fulfillment_notes?: string | null;
          tracking_number?: string | null;
          shipped_at?: string | null;
          completed_at?: string | null;
          refund_id?: string | null;
          refund_amount?: number;
          refund_status?: 'none' | 'partial' | 'full' | 'pending' | 'failed';
          refunded_at?: string | null;
          refund_reason?: string | null;
          refund_metadata?: any;
        };
      };
      refunds: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          order_id: string;
          order_number: string;
          refund_number: string;
          refund_type: 'full' | 'partial';
          refund_amount: number;
          original_amount: number;
          refund_status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
          stripe_refund_id: string | null;
          stripe_payment_intent_id: string | null;
          stripe_charge_id: string | null;
          stripe_status: string | null;
          stripe_metadata: any; // JSONB
          refund_reason: string;
          refund_category: 'customer_request' | 'wrong_item' | 'defective_product' | 'duplicate_order' | 'fraudulent' | 'other' | null;
          admin_comment: string | null;
          customer_notes: string | null;
          processed_by: string | null;
          processed_by_email: string | null;
          receipt_pdf_url: string | null;
          receipt_generated_at: string | null;
          completed_at: string | null;
          failed_at: string | null;
          failure_reason: string | null;
          metadata: any; // JSONB
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          order_id: string;
          order_number: string;
          refund_number: string;
          refund_type: 'full' | 'partial';
          refund_amount: number;
          original_amount: number;
          refund_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
          stripe_refund_id?: string | null;
          stripe_payment_intent_id?: string | null;
          stripe_charge_id?: string | null;
          stripe_status?: string | null;
          stripe_metadata?: any;
          refund_reason: string;
          refund_category?: 'customer_request' | 'wrong_item' | 'defective_product' | 'duplicate_order' | 'fraudulent' | 'other' | null;
          admin_comment?: string | null;
          customer_notes?: string | null;
          processed_by?: string | null;
          processed_by_email?: string | null;
          receipt_pdf_url?: string | null;
          receipt_generated_at?: string | null;
          completed_at?: string | null;
          failed_at?: string | null;
          failure_reason?: string | null;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          order_id?: string;
          order_number?: string;
          refund_number?: string;
          refund_type?: 'full' | 'partial';
          refund_amount?: number;
          original_amount?: number;
          refund_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
          stripe_refund_id?: string | null;
          stripe_payment_intent_id?: string | null;
          stripe_charge_id?: string | null;
          stripe_status?: string | null;
          stripe_metadata?: any;
          refund_reason?: string;
          refund_category?: 'customer_request' | 'wrong_item' | 'defective_product' | 'duplicate_order' | 'fraudulent' | 'other' | null;
          admin_comment?: string | null;
          customer_notes?: string | null;
          processed_by?: string | null;
          processed_by_email?: string | null;
          receipt_pdf_url?: string | null;
          receipt_generated_at?: string | null;
          completed_at?: string | null;
          failed_at?: string | null;
          failure_reason?: string | null;
          metadata?: any;
        };
      };
      admin_users: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          email: string;
          first_name: string | null;
          last_name: string | null;
          role: 'super_admin' | 'admin' | 'viewer';
          is_active: boolean;
          last_login: string | null;
          metadata: any; // JSONB
        };
        Insert: {
          id: string;
          created_at?: string;
          updated_at?: string;
          email: string;
          first_name?: string | null;
          last_name?: string | null;
          role?: 'super_admin' | 'admin' | 'viewer';
          is_active?: boolean;
          last_login?: string | null;
          metadata?: any; // JSONB
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          email?: string;
          first_name?: string | null;
          last_name?: string | null;
          role?: 'super_admin' | 'admin' | 'viewer';
          is_active?: boolean;
          last_login?: string | null;
          metadata?: any; // JSONB
        };
      };
      customers: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          email: string;
          phone: string | null;
          first_name: string | null;
          last_name: string | null;
          company_name: string | null;
          total_orders: number;
          total_spent: number;
          last_order_date: string | null;
          preferred_shipping_address: any | null; // JSONB
          preferred_billing_address: any | null; // JSONB
          communication_preferences: any; // JSONB
          customer_notes: string | null;
          is_active: boolean;
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          email: string;
          phone?: string | null;
          first_name?: string | null;
          last_name?: string | null;
          company_name?: string | null;
          total_orders?: number;
          total_spent?: number;
          last_order_date?: string | null;
          preferred_shipping_address?: any | null; // JSONB
          preferred_billing_address?: any | null; // JSONB
          communication_preferences?: any; // JSONB
          customer_notes?: string | null;
          is_active?: boolean;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          email?: string;
          phone?: string | null;
          first_name?: string | null;
          last_name?: string | null;
          company_name?: string | null;
          total_orders?: number;
          total_spent?: number;
          last_order_date?: string | null;
          preferred_shipping_address?: any | null; // JSONB
          preferred_billing_address?: any | null; // JSONB
          communication_preferences?: any; // JSONB
          customer_notes?: string | null;
          is_active?: boolean;
        };
      };
      customer_accounts: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          customer_id: string | null;
          auth_user_id: string | null;
          email: string;
          email_verified: boolean;
          account_status: 'active' | 'suspended' | 'disabled' | 'pending';
          first_login_at: string | null;
          last_login_at: string | null;
          login_count: number;
          notification_email: string | null;
          notification_phone: string | null;
          timezone: string;
          language: string;
          failed_login_attempts: number;
          last_failed_login_at: string | null;
          account_locked_until: string | null;
          stripe_customer_id: string | null;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_id?: string | null;
          auth_user_id?: string | null;
          email: string;
          email_verified?: boolean;
          account_status?: 'active' | 'suspended' | 'disabled' | 'pending';
          first_login_at?: string | null;
          last_login_at?: string | null;
          login_count?: number;
          notification_email?: string | null;
          notification_phone?: string | null;
          timezone?: string;
          language?: string;
          failed_login_attempts?: number;
          last_failed_login_at?: string | null;
          account_locked_until?: string | null;
          stripe_customer_id?: string | null;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_id?: string | null;
          auth_user_id?: string | null;
          email?: string;
          email_verified?: boolean;
          account_status?: 'active' | 'suspended' | 'disabled' | 'pending';
          first_login_at?: string | null;
          last_login_at?: string | null;
          login_count?: number;
          notification_email?: string | null;
          notification_phone?: string | null;
          timezone?: string;
          language?: string;
          failed_login_attempts?: number;
          last_failed_login_at?: string | null;
          account_locked_until?: string | null;
          stripe_customer_id?: string | null;
          metadata?: any;
        };
      };
      customer_sim_cards: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          customer_account_id: string;
          order_id: string | null;
          sim_card_number: string;
          iccid: string | null;
          plan_name: string;
          plan_type: 'prepaid' | 'postpaid' | 'data_only' | 'voice_data' | null;
          sim_status: 'active' | 'inactive' | 'expired' | 'suspended' | 'cancelled';
          voice_minutes_total: number;
          voice_minutes_remaining: number;
          text_messages_total: number;
          text_messages_remaining: number;
          data_mb_total: number | null;
          data_mb_remaining: number | null;
          validity_days_total: number;
          validity_days_remaining: number | null;
          purchase_date: string;
          activation_date: string | null;
          expiration_date: string | null;
          last_used_at: string | null;
          location_tracking_enabled: boolean;
          sos_global_rescue_enabled: boolean;
          voicemail_enabled: boolean;
          two_stage_dialing_enabled: boolean;
          auto_reload_enabled: boolean;
          auto_reload_threshold_percent: number | null;
          auto_reload_amount: number | null;
          auto_reload_payment_method_id: string | null;
          customer_nickname: string | null;
          customer_notes: string | null;
          original_order_data: any | null;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id: string;
          order_id?: string | null;
          sim_card_number: string;
          iccid?: string | null;
          plan_name: string;
          plan_type?: 'prepaid' | 'postpaid' | 'data_only' | 'voice_data' | null;
          sim_status?: 'active' | 'inactive' | 'expired' | 'suspended' | 'cancelled';
          voice_minutes_total?: number;
          voice_minutes_remaining?: number;
          text_messages_total?: number;
          text_messages_remaining?: number;
          data_mb_total?: number | null;
          data_mb_remaining?: number | null;
          validity_days_total: number;
          validity_days_remaining?: number | null;
          purchase_date: string;
          activation_date?: string | null;
          expiration_date?: string | null;
          last_used_at?: string | null;
          location_tracking_enabled?: boolean;
          sos_global_rescue_enabled?: boolean;
          voicemail_enabled?: boolean;
          two_stage_dialing_enabled?: boolean;
          auto_reload_enabled?: boolean;
          auto_reload_threshold_percent?: number | null;
          auto_reload_amount?: number | null;
          auto_reload_payment_method_id?: string | null;
          customer_nickname?: string | null;
          customer_notes?: string | null;
          original_order_data?: any | null;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id?: string;
          order_id?: string | null;
          sim_card_number?: string;
          iccid?: string | null;
          plan_name?: string;
          plan_type?: 'prepaid' | 'postpaid' | 'data_only' | 'voice_data' | null;
          sim_status?: 'active' | 'inactive' | 'expired' | 'suspended' | 'cancelled';
          voice_minutes_total?: number;
          voice_minutes_remaining?: number;
          text_messages_total?: number;
          text_messages_remaining?: number;
          data_mb_total?: number | null;
          data_mb_remaining?: number | null;
          validity_days_total?: number;
          validity_days_remaining?: number | null;
          purchase_date?: string;
          activation_date?: string | null;
          expiration_date?: string | null;
          last_used_at?: string | null;
          location_tracking_enabled?: boolean;
          sos_global_rescue_enabled?: boolean;
          voicemail_enabled?: boolean;
          two_stage_dialing_enabled?: boolean;
          auto_reload_enabled?: boolean;
          auto_reload_threshold_percent?: number | null;
          auto_reload_amount?: number | null;
          auto_reload_payment_method_id?: string | null;
          customer_nickname?: string | null;
          customer_notes?: string | null;
          original_order_data?: any | null;
          metadata?: any;
        };
      };
      customer_login_tokens: {
        Row: {
          id: string;
          created_at: string;
          token: string;
          email: string;
          expires_at: string;
          used_at: string | null;
          is_used: boolean;
          ip_address: string | null;
          user_agent: string | null;
          request_count: number;
          customer_account_id: string | null;
        };
        Insert: {
          id?: string;
          created_at?: string;
          token: string;
          email: string;
          expires_at?: string;
          used_at?: string | null;
          is_used?: boolean;
          ip_address?: string | null;
          user_agent?: string | null;
          request_count?: number;
          customer_account_id?: string | null;
        };
        Update: {
          id?: string;
          created_at?: string;
          token?: string;
          email?: string;
          expires_at?: string;
          used_at?: string | null;
          is_used?: boolean;
          ip_address?: string | null;
          user_agent?: string | null;
          request_count?: number;
          customer_account_id?: string | null;
        };
      };
      customer_payment_methods: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          customer_account_id: string;
          stripe_payment_method_id: string;
          stripe_customer_id: string;
          card_brand: string | null;
          card_last4: string | null;
          card_exp_month: number | null;
          card_exp_year: number | null;
          is_default: boolean;
          is_active: boolean;
          billing_address: any | null;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id: string;
          stripe_payment_method_id: string;
          stripe_customer_id: string;
          card_brand?: string | null;
          card_last4?: string | null;
          card_exp_month?: number | null;
          card_exp_year?: number | null;
          is_default?: boolean;
          is_active?: boolean;
          billing_address?: any | null;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id?: string;
          stripe_payment_method_id?: string;
          stripe_customer_id?: string;
          card_brand?: string | null;
          card_last4?: string | null;
          card_exp_month?: number | null;
          card_exp_year?: number | null;
          is_default?: boolean;
          is_active?: boolean;
          billing_address?: any | null;
          metadata?: any;
        };
      };
      sim_notifications: {
        Row: {
          id: string;
          created_at: string;
          customer_account_id: string;
          sim_card_id: string | null;
          notification_type: 'expiration_30_days' | 'expiration_14_days' | 'expiration_7_days' | 'expiration_3_days' | 'expired_1_day' | 'low_balance_20_percent' | 'low_balance_10_percent' | 'reload_confirmation' | 'welcome';
          channel: 'email' | 'sms' | 'push';
          recipient: string;
          status: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
          sent_at: string | null;
          delivered_at: string | null;
          failed_at: string | null;
          subject: string | null;
          message_preview: string | null;
          email_id: string | null;
          sms_id: string | null;
          error_message: string | null;
          retry_count: number;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          customer_account_id: string;
          sim_card_id?: string | null;
          notification_type: 'expiration_30_days' | 'expiration_14_days' | 'expiration_7_days' | 'expiration_3_days' | 'expired_1_day' | 'low_balance_20_percent' | 'low_balance_10_percent' | 'reload_confirmation' | 'welcome';
          channel: 'email' | 'sms' | 'push';
          recipient: string;
          status?: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
          sent_at?: string | null;
          delivered_at?: string | null;
          failed_at?: string | null;
          subject?: string | null;
          message_preview?: string | null;
          email_id?: string | null;
          sms_id?: string | null;
          error_message?: string | null;
          retry_count?: number;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          customer_account_id?: string;
          sim_card_id?: string | null;
          notification_type?: 'expiration_30_days' | 'expiration_14_days' | 'expiration_7_days' | 'expiration_3_days' | 'expired_1_day' | 'low_balance_20_percent' | 'low_balance_10_percent' | 'reload_confirmation' | 'welcome';
          channel?: 'email' | 'sms' | 'push';
          recipient?: string;
          status?: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
          sent_at?: string | null;
          delivered_at?: string | null;
          failed_at?: string | null;
          subject?: string | null;
          message_preview?: string | null;
          email_id?: string | null;
          sms_id?: string | null;
          error_message?: string | null;
          retry_count?: number;
          metadata?: any;
        };
      };
      notification_preferences: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          customer_account_id: string;
          sim_card_id: string | null;
          expiration_notifications_enabled: boolean;
          low_balance_notifications_enabled: boolean;
          reload_confirmations_enabled: boolean;
          marketing_emails_enabled: boolean;
          email_notifications_enabled: boolean;
          sms_notifications_enabled: boolean;
          notify_30_days_before: boolean;
          notify_14_days_before: boolean;
          notify_7_days_before: boolean;
          notify_3_days_before: boolean;
          notify_after_expiration: boolean;
          low_balance_threshold_percent: number;
          critical_balance_threshold_percent: number;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id: string;
          sim_card_id?: string | null;
          expiration_notifications_enabled?: boolean;
          low_balance_notifications_enabled?: boolean;
          reload_confirmations_enabled?: boolean;
          marketing_emails_enabled?: boolean;
          email_notifications_enabled?: boolean;
          sms_notifications_enabled?: boolean;
          notify_30_days_before?: boolean;
          notify_14_days_before?: boolean;
          notify_7_days_before?: boolean;
          notify_3_days_before?: boolean;
          notify_after_expiration?: boolean;
          low_balance_threshold_percent?: number;
          critical_balance_threshold_percent?: number;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id?: string;
          sim_card_id?: string | null;
          expiration_notifications_enabled?: boolean;
          low_balance_notifications_enabled?: boolean;
          reload_confirmations_enabled?: boolean;
          marketing_emails_enabled?: boolean;
          email_notifications_enabled?: boolean;
          sms_notifications_enabled?: boolean;
          notify_30_days_before?: boolean;
          notify_14_days_before?: boolean;
          notify_7_days_before?: boolean;
          notify_3_days_before?: boolean;
          notify_after_expiration?: boolean;
          low_balance_threshold_percent?: number;
          critical_balance_threshold_percent?: number;
          metadata?: any;
        };
      };
      customer_sessions: {
        Row: {
          id: string;
          created_at: string;
          updated_at: string;
          customer_account_id: string;
          session_token: string;
          expires_at: string;
          last_activity_at: string;
          ip_address: string | null;
          user_agent: string | null;
          device_type: string | null;
          browser: string | null;
          os: string | null;
          is_active: boolean;
          logged_out_at: string | null;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id: string;
          session_token: string;
          expires_at?: string;
          last_activity_at?: string;
          ip_address?: string | null;
          user_agent?: string | null;
          device_type?: string | null;
          browser?: string | null;
          os?: string | null;
          is_active?: boolean;
          logged_out_at?: string | null;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          updated_at?: string;
          customer_account_id?: string;
          session_token?: string;
          expires_at?: string;
          last_activity_at?: string;
          ip_address?: string | null;
          user_agent?: string | null;
          device_type?: string | null;
          browser?: string | null;
          os?: string | null;
          is_active?: boolean;
          logged_out_at?: string | null;
          metadata?: any;
        };
      };
      sim_reload_history: {
        Row: {
          id: string;
          created_at: string;
          customer_account_id: string;
          sim_card_id: string;
          order_id: string | null;
          payment_method_id: string | null;
          reload_type: 'manual' | 'auto' | 'grace_period' | null;
          voice_minutes_added: number;
          text_messages_added: number;
          data_mb_added: number | null;
          validity_days_added: number;
          base_amount: number;
          additional_features_amount: number;
          total_amount: number;
          currency: string;
          reload_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
          stripe_payment_intent_id: string | null;
          stripe_charge_id: string | null;
          processed_at: string | null;
          failed_at: string | null;
          refunded_at: string | null;
          error_message: string | null;
          metadata: any;
        };
        Insert: {
          id?: string;
          created_at?: string;
          customer_account_id: string;
          sim_card_id: string;
          order_id?: string | null;
          payment_method_id?: string | null;
          reload_type?: 'manual' | 'auto' | 'grace_period' | null;
          voice_minutes_added?: number;
          text_messages_added?: number;
          data_mb_added?: number | null;
          validity_days_added?: number;
          base_amount: number;
          additional_features_amount?: number;
          total_amount: number;
          currency?: string;
          reload_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
          stripe_payment_intent_id?: string | null;
          stripe_charge_id?: string | null;
          processed_at?: string | null;
          failed_at?: string | null;
          refunded_at?: string | null;
          error_message?: string | null;
          metadata?: any;
        };
        Update: {
          id?: string;
          created_at?: string;
          customer_account_id?: string;
          sim_card_id?: string;
          order_id?: string | null;
          payment_method_id?: string | null;
          reload_type?: 'manual' | 'auto' | 'grace_period' | null;
          voice_minutes_added?: number;
          text_messages_added?: number;
          data_mb_added?: number | null;
          validity_days_added?: number;
          base_amount?: number;
          additional_features_amount?: number;
          total_amount?: number;
          currency?: string;
          reload_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
          stripe_payment_intent_id?: string | null;
          stripe_charge_id?: string | null;
          processed_at?: string | null;
          failed_at?: string | null;
          refunded_at?: string | null;
          error_message?: string | null;
          metadata?: any;
        };
      };
    };
    Views: {
      order_summary_view: {
        Row: {
          total_orders: number;
          total_revenue: number;
          average_order_value: number;
          purchase_orders: number;
          reload_orders: number;
          shipping_orders: number;
          pending_orders: number;
          processing_orders: number;
          shipped_orders: number;
          completed_orders: number;
        };
      };
      daily_orders_view: {
        Row: {
          order_date: string;
          daily_orders: number;
          daily_revenue: number;
          daily_avg_order_value: number;
          daily_purchases: number;
          daily_reloads: number;
        };
      };
      customer_orders_view: {
        Row: {
          customer_email: string;
          customer_name: string | null;
          customer_phone: string | null;
          total_orders: number;
          total_spent: number;
          last_order_date: string;
          first_order_date: string;
          purchase_count: number;
          reload_count: number;
        };
      };
    };
    Functions: {
      get_order_stats: {
        Args: {
          start_date?: string;
          end_date?: string;
        };
        Returns: {
          total_orders: number;
          total_revenue: number;
          average_order_value: number;
          purchase_orders: number;
          reload_orders: number;
          shipping_orders: number;
          pending_orders: number;
          processing_orders: number;
          shipped_orders: number;
          completed_orders: number;
        }[];
      };
      search_orders: {
        Args: {
          search_term?: string;
          status_filter?: string;
          type_filter?: string;
          start_date?: string;
          end_date?: string;
          limit_count?: number;
          offset_count?: number;
        };
        Returns: {
          id: string;
          created_at: string;
          order_number: string;
          customer_email: string;
          customer_name: string | null;
          total_amount: number;
          order_status: string;
          order_type: string;
          has_physical_items: boolean;
          tracking_number: string | null;
        }[];
      };
    };
  };
}

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type Enums<T extends keyof Database['public']['Enums']> = Database['public']['Enums'][T];